'use client'
import React, {
  useEffect,
  useState,
  useContext,
  Dispatch,
  SetStateAction,
  useRef,
  useLayoutEffect,
  useMemo,
} from 'react'
import styles from './style.module.scss'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import '@/style/index.scss'
import EventEmitter from 'events'
import { AntdRegistry } from '@ant-design/nextjs-registry'
import { DialogHolder } from '@/components/Modal/dialog'
import CustomerService from '@/components/CustomerService'
import cls from 'classnames'
import { debounce } from 'lodash'
import { isInApp } from 'core/utils/appBridge'
import { useUpdateAppSetting } from '../appStore'
import { useResponsiveDevice } from '@/hooks/useResponsiveDevice'

type TPageLayoutProps = {
  children: React.ReactNode
  hideFooter?: boolean
  hideHeader?: boolean
  hideScrollTop?: boolean
  forceTheme?: 'dark' | 'light'
  hideCustomerService?: boolean
  className?: string
  fixedHeader?: boolean
  isResponsive?: boolean
  isPhoneShowCustomerService?: boolean
  appendToFixedTop?: React.ReactNode
  appendToFixedBottom?: React.ReactNode
  appendToFixedRightTop?: React.ReactNode
  appendToFixedRightBottom?: React.ReactNode
}

type TLayoutProps = {
  appendToFixedTopElement?: React.ReactNode
  appendToFixedBottomElement?: React.ReactNode
  appendToFixedRightTopElement?: React.ReactNode
  appendToFixedRightBottomElement?: React.ReactNode
  prependToFixedBottomElement?: React.ReactNode
}

type TLayoutContext = {
  setFixedTopElements: Dispatch<SetStateAction<React.ReactNode[]>>
  setFixedBottomElements: Dispatch<SetStateAction<React.ReactNode[]>>
  setFixedRightTopElements: Dispatch<SetStateAction<React.ReactNode[]>>
  setFixedRightBottomElements: Dispatch<SetStateAction<React.ReactNode[]>>
  resizeEvent: EventEmitter
}

const LayoutContext = React.createContext<TLayoutContext | null>(null)

export const usePageLayout = (elements?: TLayoutProps, deps: any[] = []) => {
  const layoutContext = useContext(LayoutContext)
  const [fixedTopHeight, setFixedTopHeight] = useState()
  const [fixedBottomHeight, setFixedBottomHeight] = useState()

  useEffect(() => {
    layoutContext?.resizeEvent.on('fixedTopHeightChange', (height) => {
      setFixedTopHeight(height)
    })
    layoutContext?.resizeEvent.on('fixedBottomHeightChange', (height) => {
      setFixedBottomHeight(height)
    })
    if (elements?.appendToFixedTopElement) {
      layoutContext?.setFixedTopElements((eles: React.ReactNode[]) => [...eles, elements?.appendToFixedTopElement])
    }
    if (elements?.appendToFixedBottomElement) {
      layoutContext?.setFixedBottomElements((eles: React.ReactNode[]) => [
        ...eles,
        elements?.appendToFixedBottomElement,
      ])
    }
    if (elements?.prependToFixedBottomElement) {
      layoutContext?.setFixedBottomElements((eles: React.ReactNode[]) => [
        elements?.prependToFixedBottomElement,
        ...eles,
      ])
    }
    if (elements?.appendToFixedRightBottomElement) {
      layoutContext?.setFixedRightBottomElements((eles: React.ReactNode[]) => [
        ...eles,
        elements?.appendToFixedRightBottomElement,
      ])
    }
    if (elements?.appendToFixedRightTopElement) {
      layoutContext?.setFixedRightTopElements((eles: React.ReactNode[]) => [
        ...eles,
        elements?.appendToFixedRightTopElement,
      ])
    }

    return () => {
      layoutContext?.resizeEvent.off('fixedTopHeightChange', (height) => {
        setFixedTopHeight(height)
      })
      layoutContext?.resizeEvent.off('fixedBottomHeightChange', (height) => {
        setFixedBottomHeight(height)
      })
      if (elements?.appendToFixedTopElement) {
        layoutContext?.setFixedTopElements((eles) => eles.filter((ele) => ele !== elements?.appendToFixedTopElement))
      }
      if (elements?.appendToFixedBottomElement) {
        layoutContext?.setFixedBottomElements((eles) =>
          eles.filter((ele) => ele !== elements?.appendToFixedBottomElement)
        )
      }
      if (elements?.prependToFixedBottomElement) {
        layoutContext?.setFixedBottomElements((eles) =>
          eles.filter((ele) => ele !== elements?.prependToFixedBottomElement)
        )
      }
      if (elements?.appendToFixedRightBottomElement) {
        layoutContext?.setFixedRightBottomElements((eles) =>
          eles.filter((ele) => ele !== elements?.appendToFixedRightBottomElement)
        )
      }
      if (elements?.appendToFixedRightTopElement) {
        layoutContext?.setFixedRightTopElements((eles) =>
          eles.filter((ele) => ele !== elements?.appendToFixedRightTopElement)
        )
      }
    }
  }, deps)
  return {
    fixedTopHeight,
    fixedBottomHeight,
  }
}

export default function PageLayoutProvider(props: TPageLayoutProps) {
  const [fixedTopElements, setFixedTopElements] = useState<React.ReactNode[]>([props.appendToFixedTop])
  const [fixedBottomElements, setFixedBottomElements] = useState<React.ReactNode[]>([props.appendToFixedBottom])
  const [fixedRightTopElements, setFixedRightTopElements] = useState<React.ReactNode[]>([props.appendToFixedRightTop])
  const [fixedRightBottomElements, setFixedRightBottomElements] = useState<React.ReactNode[]>([
    props.appendToFixedRightBottom,
  ])
  const [showScrollTop, setShowScrollTop] = useState(false) // 是否显示返回顶部按钮
  const fixedTopBox = useRef<HTMLDivElement>(null)
  const fixedBottomBox = useRef<HTMLDivElement>(null)
  const contentDivRef = useRef<HTMLDivElement>(null)
  const contextValueRef = useRef<TLayoutContext>({
    setFixedTopElements,
    setFixedBottomElements,
    setFixedRightTopElements,
    setFixedRightBottomElements,
    resizeEvent: new EventEmitter(),
  })

  const responsiveDevice = useResponsiveDevice()

  const shouldRenderHeader = !props.hideHeader && !isInApp()
  const shouldRenderFooter = !props.hideFooter && !isInApp()
  let shouldRenderCustomerService = false

  if (responsiveDevice === 'pc') {
    shouldRenderCustomerService = !props.hideCustomerService
  } else {
    shouldRenderCustomerService = props.isPhoneShowCustomerService || false
  }

  // 更新强制主题色
  const updateAppSetting = useUpdateAppSetting()
  updateAppSetting('forceTheme', props.forceTheme)
  useEffect(() => {
    const body = document.body
    if (props.forceTheme) {
      body.setAttribute('data-force-theme', props.forceTheme)
    } else {
      body.setAttribute('data-force-theme', '')
    }
  }, [])

  useLayoutEffect(() => {
    if (!fixedTopBox.current || !fixedBottomBox.current) return
    const resizeObserver = new ResizeObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.target === fixedBottomBox.current) {
          if (contentDivRef.current) {
            contentDivRef.current.style.paddingBottom = `${entry.contentRect.height}px`
          }
          contextValueRef.current.resizeEvent.emit('fixedBottomHeightChange', entry.contentRect.height)
        }
        if (entry.target === fixedTopBox.current) {
          if (contentDivRef.current) {
            contentDivRef.current.style.paddingTop = `${entry.contentRect.height}px`
          }
          contextValueRef.current.resizeEvent.emit('fixedTopHeightChange', entry.contentRect.height)
        }
      })
    })
    resizeObserver.observe(fixedBottomBox.current)
    resizeObserver.observe(fixedTopBox.current)
    return () => {
      resizeObserver.disconnect()
    }
  }, [fixedBottomBox.current, fixedTopBox.current])

  // layout的变更大概率不需要触发子组件的重新计算，此处避免子组件计算先
  const children = useMemo(() => props.children, [])

  // 使用 useMemo 缓存滚动处理函数
  const handleScroll = useMemo(() => {
    return debounce(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      setShowScrollTop(scrollTop > window.innerHeight)
    }, 100)
  }, [])

  // 添加滚动监听，需要在组件卸载时清理防抖函数
  useEffect(() => {
    // 初始检查滚动位置
    handleScroll()
    window.addEventListener('scroll', handleScroll)

    return () => {
      handleScroll.cancel() // 清理防抖
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  return (
    <LayoutContext.Provider value={contextValueRef.current}>
      <AntdRegistry>
        <div className={cls([styles.appLayout, props.className, { [styles.isResponsive]: props.isResponsive }])}>
          {!props.fixedHeader && shouldRenderHeader && <Header />}
          <div className={styles.fixedTop}>
            <div ref={fixedTopBox}>
              {props.fixedHeader && shouldRenderHeader && <Header />}
              {fixedTopElements}
            </div>
          </div>
          <div className={styles.appLayoutBody} id="page-layout-body" ref={contentDivRef}>
            {children}
          </div>
          {shouldRenderFooter && <Footer />}
          <div className={styles.fixedBottom}>
            <div className={styles.fixedRight}>
              {!props.hideScrollTop && showScrollTop && (
                <button
                  className={cls(styles.scrollTopBtn, styles.fixedRightButton)}
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }}
                >
                  <i className="iconfont iconfanhuidingbu" />
                </button>
              )}
              {shouldRenderCustomerService && (
                <CustomerService>
                  <button
                    className={cls(styles.customBtn, styles.fixedRightButton, {
                      [styles.customBtnHide]: !props.isPhoneShowCustomerService,
                    })}
                  >
                    <i className="iconfont iconkefu" />
                  </button>
                </CustomerService>
              )}

              {fixedRightBottomElements}
            </div>
            <div ref={fixedBottomBox}>{fixedBottomElements}</div>
          </div>
        </div>
        {/* TODO 划转Dialog弹窗标记暂时放置该处，后续重新确定位置 @edward */}
        <DialogHolder />
      </AntdRegistry>
    </LayoutContext.Provider>
  )
}
